import { InjectionKey } from 'vue';
import { RepositoryFactory, Repositories } from '@/repositories/RepositoryFactory';

const CouponRepository = RepositoryFactory.get('coupon') as Repositories['coupon'];
import { Coupon as TypeCoupon, CustomerCoupon as TypeCustomerCoupon } from '@/repositories/couponRepository';
import dayjs from 'dayjs';

export { TypeCoupon, TypeCustomerCoupon };

const CHANNEL_ID: string =
  process.env.NODE_ENV === 'local' ? window.CHANNEL_ID || process.env.VUE_APP_CHANNEL_ID : window.CHANNEL_ID;

export const useCoupon = () => {
  /**
   * 交換可能なクーポン一覧
   */
  const get = async () => {
    return await CouponRepository.get();
  };

  /**
   * 交換可能な購入クーポン一覧
   */
  const getPurchaseCoupon = async () => {
    return await CouponRepository.getPurchaseCoupon();
  };

  /**
   * ポイント -> クーポン交換
   * @param couponId
   */
  const exchange = async (couponId: string) => {
    return await CouponRepository.exchange(couponId);
  };

  /**
   * Stripeのクーポン購入リンク情報生成
   * @param couponId
   */
  const purchaseInfoGenerate = async (couponId: string) => {
    return await CouponRepository.purchase(couponId);
  };

  /**
   * クーポン詳細
   * @param couponId
   * @param [providerType]
   */
  const getDetail = async (couponId: string, providerType: string | undefined = undefined) => {
    return await CouponRepository.getDetail(couponId, providerType);
  };

  /**
   * 対象店舗一覧
   * @param couponIds
   */
  const getAvailableShops = async (couponIds: string | string[], from = 0, limit = 50) => {
    return await CouponRepository.getAvailableShops(couponIds, from, limit);
  };

  /**
   * 顧客クーポン一覧
   */
  const getCustomerCoupon = async () => {
    return await CouponRepository.getCustomerCoupon();
  };

  /**
   * 顧客クーポン詳細
   * @param customerCouponId
   */
  const getCustomerCouponDetail = async (customerCouponId: string) => {
    return await CouponRepository.getCustomerCouponDetail(customerCouponId);
  };

  /**
   * 顧客クーポン使用
   * @param customerCouponId
   * @param shopId
   */
  const useCustomerCoupon = async (customerCouponId: string, shopId?: string) => {
    return await CouponRepository.useCustomerCoupon(customerCouponId, shopId);
  };

  /**
   * クーポンアクションを実行
   * @memo 2022/05/19 アクションと呼んでいるが、実際はCouponProviderを介した付与処理を呼んでいる
   * @param actionId
   * @param token
   * @param lat CS-8234
   * @param lng CS-8234
   */
  const action = async (actionId: string, token: string, lat: number | null = null, lng: number | null = null) => {
    return await CouponRepository.action(actionId, token, lat, lng);
  };

  /**
   * 期限切れか判定
   * @note 利用可能期間開始前は期限切れ扱いにしない
   * @param {CustomerCoupon} customerCoupon
   * @returns {boolean} 期限切れの場合trueを返す
   */
  const expired = (customerCoupon: TypeCustomerCoupon) => {
    const { expires: expiresInfo } = JSON.parse(customerCoupon.couponMaster.attrs) as {
      expires: { term: number | null; startAt: string | null; endAt: string | null };
    };
    const now = Date.now();
    if (customerCoupon.expiresAt) {
      return new Date(customerCoupon.expiresAt).getTime() <= now;
    } else if (expiresInfo.endAt) {
      return new Date(expiresInfo.endAt).getTime() <= now;
    }

    return false;
  };

  /**
   * 現在日時が利用可能期間内か判定
   * @param {CustomerCoupon} customerCoupon
   * @returns {boolean} 利用可能期間内ならtrueを返す
   */
  const withinAvailablePeriod = (customerCoupon: TypeCustomerCoupon) => {
    // 期限切れの場合
    if (expired(customerCoupon)) return false;
    const { expires: expiresInfo } = JSON.parse(customerCoupon.couponMaster.attrs) as {
      expires: { term: number | null; startAt: string | null; endAt: string | null };
    };
    const now = Date.now();
    // 期間指定の場合、開始日時以降かどうか
    if (expiresInfo.startAt) {
      return new Date(expiresInfo.startAt).getTime() < now;
    }

    return true;
  };

  /**
   * 他のLINEアカウントのクーポンかどうか
   * @param customerCouponChannelId
   */
  const isOtherChannelCoupon = (customerCouponChannelId: string | null): boolean => {
    // NOTE: customerCouponChannelId が null の場合は組織クーポン
    return customerCouponChannelId ? customerCouponChannelId !== CHANNEL_ID : false;
  };

  /**
   * クーポンが有効かどうか
   * @note 利用可能期間開始前のクーポンも含むため、availableとは異なる
   */
  const valid = (customerCoupon: TypeCustomerCoupon) => {
    return (
      !customerCoupon.used &&
      !customerCoupon.disabled &&
      !expired(customerCoupon) &&
      !isOtherChannelCoupon(customerCoupon.channelId)
    );
  };

  /**
   * 顧客クーポンが使用可能状態かどうか
   */
  const available = (customerCoupon: TypeCustomerCoupon) => {
    return valid(customerCoupon) && withinAvailablePeriod(customerCoupon);
  };

  /**
   * クーポンが在庫切れかどうか
   * @returns {boolean} true:在庫切れ, false:在庫あり=交換可能
   */
  const outOfStock = (coupon: TypeCoupon) => {
    if (typeof coupon.stock !== 'number') return false;
    return coupon.stock < 1;
  };

  /**
   * 交換制限確認
   * @returns {boolean} true:交換可能, false:交換不可
   */
  const canExchange = async (coupon: TypeCoupon) => {
    return await CouponRepository.checkRestrictedCoupon(coupon);
  };

  // CS-6461 抽選対応
  /**
   * 抽選済みクーポン抽選結果を取得
   * @param providerId
   * @param token
   * @param [drawId]
   */
  const getInstantwinResult = async (providerId: string, token: string, drawId?: string) => {
    return await CouponRepository.getInstantwinResult(providerId, token, drawId);
  };
  /**
   * 抽選する
   * @param providerId
   * @param token
   */
  const drawInstantwin = async (providerId: string, token: string) => {
    return await CouponRepository.drawInstantwin(providerId, token);
  };
  /**
   * 抽選ポストバックを実行する
   * @param providerId
   * @param [drawId]
   */
  const triggerWithInstantwinResult = async (providerId: string, drawId?: string) => {
    return await CouponRepository.triggerWithInstantwinResult(providerId, drawId);
  };

  /**
   * 指定したIDのクーポンプロバイダーを取得
   * @param providerId
   */
  const getCouponProviderById = async (providerId: string) => {
    return await CouponRepository.getCouponProviderById(providerId);
  };

  /**
   * 交換制限の文言をラベルをを返す
   * @param restrictionSettings
   * @returns {string} 制限の文言
   */
  const getRestrictionSettingLabel = (restrictionSettings: any) => {
    const settings = JSON.parse(restrictionSettings);
    if (!settings) return '';
    const { type, value } = settings.customer || {};
    switch (type) {
      case 'ONCE':
        return '１回だけ交換可能';
      case 'DAILY':
        return '毎日1回交換可能' + (value ? `\n(${value}更新)` : '');
      case 'TERM': {
        const termValue: string[] = [value || []].flat();
        return `期間中1回だけ交換可能 ${termValue.map(v => dayjs(v).format('YYYY/MM/DD HH:mm')).join('〜')}`;
      }
      default:
        return '';
    }
  };

  const getTerm = (restrictionSettings: any): dayjs.Dayjs[] | null => {
    const settings = restrictionSettings;
    if (!settings) return null;
    const { type, value } = settings.customer || {};
    if (type !== 'TERM') return null;
    const termValue: string[] = [value || []].flat();
    return termValue.map(v => dayjs(v));
  };

  const withinTerm = (restrictionSettings: any): boolean => {
    const termResult = getTerm(restrictionSettings);
    if (!termResult) return false;
    const [from, to] = termResult;
    if (!from || !to) return false;
    const now = dayjs();
    return from.isBefore(now) && to.isAfter(now);
  };

  return {
    get,
    getPurchaseCoupon,
    exchange,
    purchaseInfoGenerate,
    getDetail,
    getAvailableShops,
    getCustomerCoupon,
    getCustomerCouponDetail,
    useCustomerCoupon,
    action,

    available,
    valid,
    isOtherChannelCoupon,
    outOfStock,
    canExchange,

    // CS-6461
    getInstantwinResult,
    drawInstantwin,

    // CS-6905
    triggerWithInstantwinResult,

    getCouponProviderById,
    getRestrictionSettingLabel,
    getTerm,
    withinTerm,
  };
};

type CouponType = ReturnType<typeof useCoupon>;
export const CouponKey: InjectionKey<CouponType> = Symbol('Coupon');
